/**
 * csvParser.js
 * Responsable du parsing et validation des fichiers CSV bruts
 */

import { COLLABORATORS, ConfigUtils } from './config.js';

const CSVParser = (() => {
  /**
   * Parser le contenu brut CSV et retourner un array d'objets normalisés
   * @param {string} fileContent - Contenu du fichier CSV
   * @param {string} structure - ID de la structure
   * @returns {Object} { success: boolean, data: Array, errors: Array }
   */
  function parse(fileContent, structure) {
    const errors = [];
    const data = [];

    if (!fileContent || fileContent.trim().length === 0) {
      return {
        success: false,
        data: [],
        errors: [{ row: 0, message: 'Fichier vide' }]
      };
    }

    // Déterminer le séparateur (virgule ou point-virgule)
    const separator = detectSeparator(fileContent);
    const lines = fileContent.split('\n').filter(line => line.trim());

    if (lines.length < 2) {
      return {
        success: false,
        data: [],
        errors: [{ row: 0, message: 'Fichier doit contenir au moins une ligne de header + une donnée' }]
      };
    }

    // Parser le header
    const headerLine = lines[0];
    const headers = parseCSVLine(headerLine, separator);
    const headerMap = mapHeaders(headers);

    if (!headerMap.success) {
      return {
        success: false,
        data: [],
        errors: [{ row: 0, message: `Headers invalides: ${headerMap.error}` }]
      };
    }

    // Parser les lignes de données
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Ignorer les lignes vides

      const values = parseCSVLine(line, separator);
      const entry = parseRow(values, headerMap.map, structure, i + 1, errors);

      if (entry) {
        data.push(entry);
      }
    }

    return {
      success: errors.length === 0 || data.length > 0,
      data,
      errors
    };
  }

  /**
   * Détecter si le CSV utilise virgule ou point-virgule
   */
  function detectSeparator(content) {
  const line = content.split('\n')[0];
  const commaCount = (line.match(/,/g) || []).length;
  const semicolonCount = (line.match(/;/g) || []).length;
  const tabCount = (line.match(/\t/g) || []).length;
  
  // Si beaucoup de tabs, c'est du tab-separated
  if (tabCount > 2) {
    return '\t';
  }
  
  // Sinon, virgule ou point-virgule
  return semicolonCount > commaCount ? ';' : ',';
}

  /**
   * Parser une ligne CSV (gère les guillemets)
   */
  function parseCSVLine(line, separator = ',') {
    const result = [];
    let current = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          // Guillemets échappés
          current += '"';
          i++;
        } else {
          // Toggle guillemets
          insideQuotes = !insideQuotes;
        }
      } else if (char === separator && !insideQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  }

  /**
   * Mapper les headers CSV aux champs attendus
   */
  function mapHeaders(headers) {
    const expectedFields = {
      collaborator: ['nom du collaborateur', 'name', 'nom'],
      date: ['date'],
      client: ['client'],
      activity: ['activité', 'activity'],
      hours: ['somme', 'hours', 'time'],
      ttv: ['ttv'],
      tr: ['tr'],
      absence: ['absence']
    };

    const map = {};
    let missing = [];

    for (const [field, names] of Object.entries(expectedFields)) {
      let found = false;
      for (let i = 0; i < headers.length; i++) {
        const headerNormalized = headers[i]
          .toLowerCase()
          .trim()
          .replace(/[^\w]/g, '');

        for (const name of names) {
          const nameNormalized = name.toLowerCase().replace(/[^\w]/g, '');
          if (headerNormalized === nameNormalized) {
            map[field] = i;
            found = true;
            break;
          }
        }
        if (found) break;
      }

      if (!found && ['collaborator', 'date', 'client', 'activity', 'hours'].includes(field)) {
        missing.push(field);
      }
    }

    if (missing.length > 0) {
      return {
        success: false,
        error: `Colonnes manquantes: ${missing.join(', ')}`
      };
    }

    return { success: true, map };
  }

  /**
   * Parser une ligne de données
   */
  function parseRow(values, headerMap, structure, rowNum, errors) {
    try {
      // Extraire les valeurs
      const collaboratorName = values[headerMap.collaborator];
      const dateStr = values[headerMap.date];
      const rawClient = values[headerMap.client];
      const client = (!rawClient || !rawClient.trim()) ? 'Autres' : rawClient.trim();
      const activity = values[headerMap.activity];
      const hoursStr = values[headerMap.hours];

      // Validations
      if (!collaboratorName || !collaboratorName.trim()) {
        errors.push({ row: rowNum, message: 'Nom collaborateur vide' });
        return null;
      }

      if (!dateStr || !dateStr.trim()) {
        errors.push({ row: rowNum, message: 'Date vide' });
        return null;
      }

      // Parser la date (DD/MM/YYYY)
      const date = parseDate(dateStr);
      if (!date) {
        errors.push({ row: rowNum, message: `Format date invalide: "${dateStr}" (attendu: DD/MM/YYYY)` });
        return null;
      }

      if (!activity || !activity.trim()) {
        errors.push({ row: rowNum, message: 'Activité vide' });
        return null;
      }

      // Parser les heures
      const hours = parseFloat(hoursStr);
      if (isNaN(hours) || hours < 0 || hours > 1) {
        errors.push({ row: rowNum, message: `Heures invalides: "${hoursStr}" (doit être entre 0 et 1)` });
        return null;
      }

      // Trouver le collaborateur (dans toutes les structures)
      const collaborator = ConfigUtils.findCollaboratorByName(collaboratorName);
      if (!collaborator) {
        errors.push({ row: rowNum, message: `Collaborateur non trouvé: "${collaboratorName}"` });
        return null;
      }

      // Parser les flags optionnels
      const ttv = headerMap.ttv !== undefined ? parseInt(values[headerMap.ttv]) || 0 : 0;
      const tr = headerMap.tr !== undefined ? parseInt(values[headerMap.tr]) || 0 : 0;
      const absence = headerMap.absence !== undefined ? parseInt(values[headerMap.absence]) || 0 : 0;

      // Créer l'entrée
      return {
        id: generateId(),
        date: date,
        collaborator: collaborator.id,
        collaboratorName: collaborator.name,
        client: client.trim(),
        activity: activity.trim(),
        hours: hours,
        ttv: ttv,
        tr: tr,
        absence: absence === 1,
        structure: collaborator.structure, // use the collaborator's actual structure
        importedAt: new Date().toISOString()
      };

    } catch (error) {
      errors.push({ row: rowNum, message: `Erreur parsing: ${error.message}` });
      return null;
    }
  }

  /**
   * Parser date au format DD/MM/YYYY → YYYY-MM-DD
   */
  function parseDate(dateStr) {
    const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = dateStr.match(regex);

    if (!match) return null;

    const day = parseInt(match[1]);
    const month = parseInt(match[2]);
    const year = parseInt(match[3]);

    // Validation basique
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return null;
    }

    // Retourner au format ISO
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  /**
   * Générer un ID unique pour l'entrée
   */
  function generateId() {
    return `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  return {
    parse
  };
})();

export default CSVParser;
